/**
 * @typedef {{
 *   aws: {
 *     apiGateway: {
 *       checkoutService: string,
 *       merchantBackend: string,
 *       merchantProduct: string,
 *       merchantCustomer: string,
 *       merchantMessaging: string,
 *       promotionService: string,
 *       shopperReportService: string,
 *       shopifyService: string,
 *       trackingService: string,
 *       upcdealService: string,
 *     },
 *     region: string,
 *     s3: {
 *       uploadBucket: string
 *     }
 *   },
 *   app: {
 *      email: {
 *          hello: string,
 *      },
 *   },
 *   cognito: {
 *     appClientId: string,
 *     identityPoolId: string,
 *     userPoolId: string,
 *     region: string,
 *     auth: {
 *       ttl: number,
 *       mfaEnabled: boolean
 *     }
 *   },
 *   debug: {
 *     enabled: boolean,
 *     verbosity: number
 *   },
 *   domainToSource: Object,
 *   knownSources: string[],
 *   timezone: string,
 *   format: {
 *     date: {
 *       default: string,
 *       datePicker: string,
 *       lambdaRequest: string
 *     },
 *     datetime: {
 *       datePicker: string
 *     }
 *     time: {
 *       default: string
 *       datePicker: string
 *     },
 *   },
 *   messages: {
 *     demoModeMessage
 *   },
 *   stripe: {
 *       api_key: string,
 *   },
 *   shopify: {
 *       application: { url: string, auth_url: string },
 *   },
 * }} HhConfig
 */

const domainToSource = {
    halston: 'halston',
    chillibeans: 'chilli_beans',
};

/**
 * @type HhConfig
 */
const config = {
    aws: {
        apiGateway: {
            billingService: () => { return process.env.HH_AWS_API_GATEWAY_BILLING_SERVICE; },
            checkoutService: () => { return process.env.HH_AWS_API_GATEWAY_CHECKOUT_SERVICE; },
            merchantBackend: () => { return process.env.HH_AWS_API_GATEWAY_MERCHANT_BACKEND; },
            merchantProduct: () => { return process.env.HH_AWS_API_GATEWAY_MERCHANT_PRODUCT; },
            merchantCustomer: () => { return process.env.HH_AWS_API_GATEWAY_MERCHANT_CUSTOMER; },
            merchantMessaging: () => { return process.env.HH_AWS_API_GATEWAY_MERCHANT_MESSAGING; },
            promotionService: () => { return process.env.HH_AWS_API_GATEWAY_PROMOTION_SERVICE; },
            shopperReportService: () => { return process.env.HH_AWS_API_GATEWAY_SHOPPER_REPORT_SERVICE; },
            shopifyService: () => { return process.env.HH_AWS_API_GATEWAY_SHOPIFY_SERVICE; },
            trackingService: () => { return process.env.HH_AWS_API_GATEWAY_TRACKING_SERVICE; },
            upcdealService: () => { return process.env.HH_AWS_API_GATEWAY_UPCDEAL_SERVICE; },
        },
        region: () => { return process.env.HH_AWS_REGION; },
        s3: {
            uploadBucket: 'app-uploads',
        },
    },
    app: {
        email: {
            hello: 'hello@haftahave.com',
        },
    },
    cognito: {
        appClientId: () => { return process.env.COGNITO_APP_CLIENT_ID; },
        identityPoolId: () => { return process.env.COGNITO_IDENTITY_POOL_ID; },
        userPoolId: () => { return process.env.COGNITO_USER_POOL_ID; },
        region: () => { return process.env.COGNITO_REGION; },
        auth: {
            ttl: () => { return Number(process.env.COGNITO_AUTH_TTL); },
            mfaEnabled: () => { return Number(process.env.COGNITO_AUTH_MFA_ENABLED) === 1; },
        },
    },
    debug: {
        enabled: () => { return Number(process.env.HH_DEBUG_ENABLED) === 1; },
        verbosity: () => { return Number(process.env.HH_DEBUG_VERBOSITY); },
    },
    domainToSource,
    format: {
        date: {
            default: 'MMM DD YYYY', // used for moment
            datePicker: 'MM-dd-yyyy',
            lambdaRequest: 'YYYY-MM-DD',
        },
        datetime: {
            datePicker: 'MM-dd-yyyy, h:mm aa z',
        },
        time: {
            default: 'h:mm A z',
            datePicker: 'h:mm aa',
        },
    },
    knownSources: Object.values(domainToSource),
    messages: {
        demoModeMessage: 'YOUR ACCOUNT IS NOT VERIFIED.\n\n'
            + 'The information shown here is only an example.\n'
            + 'Please check the inbox of the email used to create your account and verify your account.'
            + ' Once your account is verified, it will be activated and real-time data collection will begin.'
            + ' If you need assistance, email us at hello@haftahave.com',
    },
    stripe: {
        api_key: () => { return process.env.STRIPE_API_KEY; },
    },
    shopify: {
        application: {
            url: () => { return process.env.SHOPIFY_APPLICATION_URL; },
            auth_url: () => { return process.env.SHOPIFY_AUTH_URL; },
        },
    },
    timezone: 'America/Los_Angeles',
};

/**
 * @param {Object} object
 * @returns {Object}
 */
const resolveValuesForObject = (object) => {
    const result = object;
    Object.keys(object).forEach((key) => {
        if (typeof object[key] === 'function') {
            result[key] = object[key]();
        }
        if (typeof object[key] === 'object') {
            result[key] = resolveValuesForObject(object[key]);
        }
    });
    return result;
};

export default new Proxy(config, {
    get: (target, name /* receiver */) => {
        let propertyValue = target[name];

        if (typeof propertyValue === 'function') {
            propertyValue = propertyValue();
        }
        if (typeof propertyValue === 'object') {
            propertyValue = resolveValuesForObject(propertyValue);
        }
        return propertyValue;
    },
});
